import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import './Navigation.css';

const Navigation = () => {
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading, logout, authConfigured } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const productsDropdownRef = useRef(null);
  const aboutDropdownRef = useRef(null);

  const isGoraiSection = location.pathname.startsWith('/gorai');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productsDropdownRef.current && !productsDropdownRef.current.contains(event.target)) {
        setProductsOpen(false);
      }
      if (aboutDropdownRef.current && !aboutDropdownRef.current.contains(event.target)) {
        setAboutOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setProductsOpen(false);
    setAboutOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const products = [
    {
      name: 'Gorai',
      path: '/gorai',
      icon: '🤖',
      description: 'Robotics Framework',
      status: 'In Development',
      hasImage: true,
      image: '/images/gorai.png',
    },
  ];

  const aboutItems = [
    {
      name: 'Company',
      path: '/company',
      icon: '🏢',
      description: 'About Emerging Robotics'
    },
    {
      name: 'Founders',
      path: '/founders',
      icon: '👥',
      description: 'Meet the Team'
    },
  ];

  return (
    <motion.nav
      className={`navigation ${scrolled ? 'scrolled' : ''} ${isGoraiSection ? 'gorai-theme' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          <img
            src="/er-logo.svg"
            alt=""
            className="logo-icon-img"
          />
          <span className="logo-text">Emerging Robotics</span>
        </Link>

        <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <Link to="/" className={location.pathname === '/' ? 'active' : ''}>
            Home
          </Link>

          {/* Products Dropdown */}
          <div className="nav-dropdown" ref={productsDropdownRef}>
            <button
              className={`nav-dropdown-trigger ${isGoraiSection ? 'active' : ''}`}
              onClick={() => setProductsOpen(!productsOpen)}
              aria-expanded={productsOpen}
            >
              Products
              <span className={`dropdown-arrow ${productsOpen ? 'open' : ''}`}>▾</span>
            </button>

            <AnimatePresence>
              {productsOpen && (
                <motion.div
                  className="nav-dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {products.map((product) => (
                    <Link
                      key={product.path}
                      to={product.path}
                      className="dropdown-item"
                      onClick={() => setProductsOpen(false)}
                    >
                      {product.hasImage ? (
                        <img src={product.image} alt="" className="dropdown-icon-img" />
                      ) : (
                        <span className="dropdown-icon">{product.icon}</span>
                      )}
                      <div className="dropdown-info">
                        <span className="dropdown-name">
                          {product.name}
                        </span>
                        <span className="dropdown-desc">{product.description}</span>
                      </div>
                      <span className="dropdown-status">{product.status}</span>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/services" className={location.pathname === '/services' ? 'active' : ''}>
            Services
          </Link>

          <Link to="/license" className={location.pathname === '/license' ? 'active' : ''}>
            License
          </Link>

          <Link to="/blog" className={location.pathname.startsWith('/blog') ? 'active' : ''}>
            Blog
          </Link>

          {/* About Dropdown */}
          <div className="nav-dropdown" ref={aboutDropdownRef}>
            <button
              className={`nav-dropdown-trigger ${location.pathname === '/company' || location.pathname === '/founders' ? 'active' : ''}`}
              onClick={() => setAboutOpen(!aboutOpen)}
              aria-expanded={aboutOpen}
            >
              About
              <span className={`dropdown-arrow ${aboutOpen ? 'open' : ''}`}>▾</span>
            </button>

            <AnimatePresence>
              {aboutOpen && (
                <motion.div
                  className="nav-dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {aboutItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="dropdown-item"
                      onClick={() => setAboutOpen(false)}
                    >
                      <span className="dropdown-icon">{item.icon}</span>
                      <div className="dropdown-info">
                        <span className="dropdown-name">{item.name}</span>
                        <span className="dropdown-desc">{item.description}</span>
                      </div>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a href="https://github.com/emergingrobotics" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>

          {/* Mobile Auth Section */}
          <div className="nav-auth-mobile">
            <ThemeToggle />
            {authConfigured && (
              authLoading ? (
                <span className="auth-loading-indicator">...</span>
              ) : isAuthenticated ? (
                <div className="user-menu-mobile">
                  <span className="user-email">{user?.email}</span>
                  <button onClick={handleLogout} className="btn-logout">
                    Sign Out
                  </button>
                </div>
              ) : (
                <Link to="/login" className="btn-login">
                  Sign In
                </Link>
              )
            )}
          </div>
        </div>

        <div className="nav-actions">
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Desktop Auth Section */}
          <div className="nav-auth">
            {authConfigured && (
              authLoading ? (
                <span className="auth-loading-indicator">...</span>
              ) : isAuthenticated ? (
                <div className="user-menu">
                  <span className="user-email">{user?.email}</span>
                  <button onClick={handleLogout} className="btn-logout">
                    Sign Out
                  </button>
                </div>
              ) : (
                <Link to="/login" className="btn-login">
                  Sign In
                </Link>
              )
            )}
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navigation;
